export const ASSIGNMENT_REQUEST_TIMEOUT_MS = 30000;

// Bound both assignment writes and their read-only reconciliation. The caller
// keeps its edits when the server outcome is uncertain; abort is not an undo.
export async function withAssignmentRequestDeadline(operation, { signal, abortOnTimeout = true, timeoutMessage } = {}) {
  const controller = new AbortController();
  if (signal?.aborted) throw new DOMException('Request cancelled', 'AbortError');
  let timeoutError;
  const throwIfExpired = () => {
    if (timeoutError) throw timeoutError;
    if (controller.signal.aborted) throw new DOMException('Request cancelled', 'AbortError');
  };
  let rejectCancelled;
  const cancelled = new Promise((_, reject) => { rejectCancelled = reject; });
  const abort = () => {
    rejectCancelled(new DOMException('Request cancelled', 'AbortError'));
    controller.abort();
  };
  signal?.addEventListener('abort', abort, { once: true });
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      timeoutError = Object.assign(new Error(timeoutMessage?.() || 'The assignment save could not be confirmed. Please retry.'), {
        status: 408, name: 'TimeoutError',
      });
      reject(timeoutError);
      if (abortOnTimeout) controller.abort();
    }, ASSIGNMENT_REQUEST_TIMEOUT_MS);
  });
  const stopForwardingCancellation = () => signal?.removeEventListener('abort', abort);
  let transport;
  try {
    transport = Promise.resolve(operation(controller.signal, throwIfExpired));
    // A write can outlive its caller's deadline. Continue forwarding explicit
    // cancellation until that transport settles, not just until the UI returns.
    transport.then(stopForwardingCancellation, stopForwardingCancellation);
    return await Promise.race([
      transport, deadline, cancelled,
    ]);
  } finally {
    clearTimeout(timer);
    if (!transport) stopForwardingCancellation();
  }
}
